import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { getSubdomainInfo } from '../utils/subdomain';

interface State {
  id: string;
  name: string;
  uf: string;
}

interface City {
  id: string;
  name: string;
  state: string;
}

function maskCpf(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return d.replace(/^(\d{3})(\d+)/, '$1.$2');
  if (d.length <= 9) return d.replace(/^(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
  return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

function maskDate(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return d.replace(/^(\d{2})(\d+)/, '$1/$2');
  return d.replace(/^(\d{2})(\d{2})(\d+)/, '$1/$2/$3');
}

function maskPhone(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10)
    return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
}

function isValidCpf(cpf: string): boolean {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += parseInt(d[i]) * (10 - i);
  let r = (s * 10) % 11;
  if (r === 10) r = 0;
  if (r !== parseInt(d[9])) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += parseInt(d[i]) * (11 - i);
  r = (s * 10) % 11;
  if (r === 10) r = 0;
  return r === parseInt(d[10]);
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState({ name: '', email: '', password: '', cpf: '', birthDate: '', phone: '' });
  const [hasWhatsapp, setHasWhatsapp] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [loadingCities, setLoadingCities] = useState(false);
  const subdomain = useRef(getSubdomainInfo());

  useEffect(() => {
    api.get('/states').then(({ data }) => {
      setStates(data);
      const sub = getSubdomainInfo();
      if (sub) {
        const match = data.find((s: State) => s.uf === sub.stateUf);
        if (match) setSelectedState(match.uf);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedState) { setCities([]); setSelectedCity(''); return; }
    setLoadingCities(true);
    api.get(`/cities/contratados?state=${selectedState}`)
      .then(({ data }) => {
        setCities(data);
        const sub = getSubdomainInfo();
        if (sub) {
          const match = data.find((c: City) => c.name.toLowerCase() === sub.citySlug);
          if (match) setSelectedCity(match.id);
        }
      })
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [selectedState]);

  const handlePrecheck = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErr('');
    setInfo('');
    if (!isValidCpf(form.cpf)) { setErr('CPF inválido'); return; }
    if (!form.email) { setErr('Informe seu e-mail'); return; }
    if (!selectedCity) { setErr('Selecione um estado e uma cidade'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/precheck', { email: form.email, cpf: form.cpf, cityId: selectedCity });
      if (data.status === 'new') {
        setStep(2);
      } else if (data.status === 'exists') {
        setInfo(data.message || 'Este email já está cadastrado. Você já está cadastrado, mas pode vincular sua conta a esta cidade.');
        navigate(`/verify?email=${encodeURIComponent(form.email)}`, { state: { message: data.message } });
      } else if (data.status === 'already-linked') {
        setErr(data.message || 'Este email já está cadastrado nesta cidade.');
      } else if (data.status === 'cpf-exists') {
        setErr(data.message || 'CPF já cadastrado.');
      }
    } catch (err: any) {
      setErr(err.response?.data?.message || 'Erro ao verificar cadastro');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (form.password.length < 6) { setErr('A senha deve ter pelo menos 6 caracteres'); return; }
    if (!selectedCity) { setErr('Selecione um estado e uma cidade'); return; }
    if (!isValidCpf(form.cpf)) { setErr('CPF inválido'); return; }
    setLoading(true);
    try {
      const [dd, mm, yyyy] = form.birthDate.split('/');
      const payload = { ...form, birthDate: `${yyyy}-${mm}-${dd}`, cityId: selectedCity, whatsapp: hasWhatsapp ? form.phone : undefined };
      await api.post('/auth/register', payload);
      navigate(`/verify?email=${encodeURIComponent(form.email)}`);
    } catch (e: any) {
      setErr(e.response?.data?.message || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value });
  const handleCpf = (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, cpf: maskCpf(e.target.value) });
  const handlePhone = (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, phone: maskPhone(e.target.value) });
  const handleBirthDate = (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, birthDate: maskDate(e.target.value) });

  const isFromSubdomain = subdomain.current !== null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">Criar Conta</h1>
        <p className="text-sm text-gray-500 text-center mb-6">
          {step === 1 ? 'Informe seu e-mail e CPF para começar' : 'Preencha os dados para se cadastrar no GeoZ'}
        </p>
        {err && <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm mb-4">{err}</div>}
        {info && <div className="bg-blue-50 text-blue-700 p-3 rounded-md text-sm mb-4">{info}</div>}

        {step === 1 && (
          <form onSubmit={handlePrecheck} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">CPF</label>
              <input type="text" value={form.cpf} onChange={handleCpf} required placeholder="000.000.000-00" maxLength={14}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">E-mail</label>
              <input type="email" value={form.email} onChange={set('email')} required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Estado</label>
              <select value={selectedState}
                onChange={isFromSubdomain ? undefined : (e) => { setSelectedState(e.target.value); setSelectedCity(''); }}
                required disabled={isFromSubdomain}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-white disabled:bg-gray-100">
                <option value="">Selecione...</option>
                {states.map((s) => <option key={s.id} value={s.uf}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Cidade</label>
              <select value={selectedCity}
                onChange={isFromSubdomain ? undefined : (e) => setSelectedCity(e.target.value)}
                required disabled={isFromSubdomain || !selectedState || loadingCities}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-white disabled:bg-gray-100">
                <option value="">{loadingCities ? 'Carregando...' : 'Selecione...'}</option>
                {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50">
              {loading ? 'Verificando...' : 'Continuar'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nome Completo</label>
              <input type="text" value={form.name} onChange={set('name')} required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Data de Nascimento</label>
              <input type="text" inputMode="numeric" value={form.birthDate} onChange={handleBirthDate} required
                placeholder="DD/MM/AAAA" maxLength={10}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Senha</label>
              <input type="password" value={form.password} onChange={set('password')} required minLength={6}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Telefone Celular</label>
              <input type="tel" value={form.phone} onChange={handlePhone} required placeholder="(47) 99999-9999" maxLength={16}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={hasWhatsapp} onChange={(e) => setHasWhatsapp(e.target.checked)}
                className="rounded border-gray-300" />
              Possuo WhatsApp
            </label>
            <button type="submit" disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50">
              {loading ? 'Cadastrando...' : 'Cadastrar'}
            </button>
            <button type="button" onClick={() => setStep(1)} disabled={loading}
              className="w-full text-sm text-gray-500 hover:text-gray-700 py-1">
              Voltar
            </button>
          </form>
        )}

        <p className="text-sm text-gray-500 text-center mt-4">
          Já tem conta? <Link to="/login" className="text-primary-600 hover:text-primary-700">Entrar</Link>
        </p>
      </div>
    </div>
  );
}
